import { DownOutlined } from '@ant-design/icons';
import { Form, InputNumber, Select } from 'antd';
import TrashV2 from 'assets/Icons/trashV2';
import Box from 'components/Box';
import Text from 'components/Text';
import colorTheme from 'constants/color';
import { LOYALTY_STAMPS_REWARD_TYPE } from 'features/settingService/services/constants';
import { ChevronDown } from 'lucide-react';
import styled from 'styled-components';

const rewardTypesOptions = [
  {
    value: LOYALTY_STAMPS_REWARD_TYPE.PERCENTAGE_DISCOUNT,
    label: 'Percentage Discount',
  },
  {
    value: LOYALTY_STAMPS_REWARD_TYPE.AMOUNT_DISCOUNT,
    label: 'Amount Discount',
  },
  { value: LOYALTY_STAMPS_REWARD_TYPE.FREE_SERVICE, label: 'Free Service' },
];

interface RewardRowProps {
  field: {
    key: number;
    name: number;
    fieldKey?: number;
  };
  remove: (index: number | number[]) => void;
  onClickChooseService: () => void;
}

const RewardRow = ({ field, remove, onClickChooseService }: RewardRowProps) => {
  return (
    <Box className='reward-row'>
      {/* Stamps Required */}
      <Form.Item
        {...field}
        name={[field.name, 'stampsRequired']}
        rules={[
          {
            required: true,
            message: 'Please input stamps required',
          },
        ]}
      >
        <InputNumber
          min={1}
          controls={false}
          placeholder='Enter stamps required'
        />
      </Form.Item>

      {/* Reward Type */}
      <Form.Item {...field} name={[field.name, 'rewardType']}>
        <Select suffixIcon={<ChevronDown />} options={rewardTypesOptions} popupClassName="reward-select-dropdown"/>
      </Form.Item>

      {/* Discount / Service */}
      <Form.Item noStyle shouldUpdate>
        {(form) => {
          const rewardType = form.getFieldValue([
            'rewards',
            field.name,
            'rewardType',
          ]);

          if (rewardType === LOYALTY_STAMPS_REWARD_TYPE.FREE_SERVICE) {
            return (
              <Form.Item
                {...field}
                name={[field.name, 'serviceId']}
                rules={[{ required: true, message: 'Please select a service' }]}
              >
                <ChooseSerice
                  onClick={onClickChooseService}
                  $error={
                    form.getFieldError(['rewards', field.name, 'serviceId'])
                      .length > 0
                  }
                >
                  <Text
                    variant='H5'
                    style={{ fontWeight: 500 }}
                    className='service-name'
                  >
                    {form.getFieldValue([
                      'rewards',
                      field.name,
                      'serviceName',
                    ]) || 'Select Service'}
                  </Text>
                  <ChevronDown />
                </ChooseSerice>
              </Form.Item>
            );
          }

          if (rewardType === LOYALTY_STAMPS_REWARD_TYPE.AMOUNT_DISCOUNT) {
            return (
              <Form.Item
                {...field}
                name={[field.name, 'discountValue']}
                rules={[
                  { required: true, message: 'Please input discount value' },
                ]}
              >
                <InputNumber
                  min={1}
                  controls={false}
                  className='discount-input'
                  addonAfter='$'
                  placeholder='Enter amount discount'
                />
              </Form.Item>
            );
          }

          return (
            <Form.Item
              {...field}
              name={[field.name, 'discountValue']}
              rules={[
                { required: true, message: 'Please input discount value' },
              ]}
            >
              <InputNumber
                min={0}
                max={100}
                controls={false}
                className='discount-input'
                addonAfter='%'
                placeholder='Enter discount'
              />
            </Form.Item>
          );
        }}
      </Form.Item>

      {/* Actions */}
      <button type='button' onClick={() => remove(field.name)}>
        <TrashV2 width={32} height={32} fill={colorTheme.v2_primary_bg_6} />
      </button>
    </Box>
  );
};

export default RewardRow;

const ChooseSerice = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid
    ${(props) => (props.$error ? colorTheme.error_6 : colorTheme.v2_border_3)};
  height: 54px;
  border-radius: 4px;
  padding: 0 16px;
  cursor: pointer;

  .service-name {
    font-weight: 500;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
